import { defineComponent as _defineComponent } from 'vue'
import { computed, onMounted, ref, watch } from 'vue'
import { uniqueId } from 'lodash'
import {
    ConditionsEvaluationType,
    KhFormulaManager,
    KhFormulaSchemaRoot,
    KhRulesEngineRule,
    KhSelect,
    LogicalComparisonOperator,
    Nullable,
    Snack,
    SnackColor,
    Undefinable,
} from '@internal-libraries/kheops-ui-lib'
import { useGroupStore } from '../../../../../dPetablissement/vue/Store/GroupStore'
import { ALL_ESTABLISHMENT_ID, useConfigStore } from '../../../Store/configStore'
import Group from '../../../../../dPetablissement/vue/Classes/Group'
import { ConfigModule } from '../type/ConfigModule'
import { useSnackStore } from '../../../Store/snackStore'
import { ConfigElementComponentType } from '../enum/ConfigType'
import { GLOBAL_GROUP_KEY } from '../const'
import CommonHelper from '../../../Classes/CommonHelper'
import { useClvrTranslation } from '../../../../../../core/vue/composables/useClvrTranslation'
import { ClvrPrimaryColor } from '../../../../../../core/vue/constants/ClvrBaseThemeColors'
import ClvrFloatingCard from '../../../../../../core/vue/components/ClvrFloatingCard/ClvrFloatingCard.vue'

export interface ClvrConfigProps {
    module: string
}


export default /*#__PURE__*/_defineComponent({
  __name: 'ClvrConfig',
  props: {
    module: null
  },
  setup(__props: any) {

const props = __props as { module: string }



const configValuePropertyByType = {
    global: 'global',
    default: 'default',
    group: 'value',
}

const groupStore = useGroupStore()
const configStore = useConfigStore()
const snackStore = useSnackStore()
const { $t } = useClvrTranslation(['clevehr', 'system', props.module])
const formValues = ref<Record<string, unknown>>({})

const allEstablishment = computed<Group>(
    () => new Group({ id: ALL_ESTABLISHMENT_ID, text: $t.value('system:system-config-all_groups') }),
)
const establishments = computed<Group[]>(() => {
    return [allEstablishment.value, ...groupStore.groups].sort((a, b) => {
        if (a.id === ALL_ESTABLISHMENT_ID) {
            return -1
        }
        if (b.id === ALL_ESTABLISHMENT_ID) {
            // having only a comparison for 'a.id' is not sufficient in Chrome browser...
            return 1
        }
        return CommonHelper.compareTwoStrings(a.text, b.text)
    })
})

const moduleConfig = computed<Undefinable<ConfigModule>>(() => {
    return configStore.modulesConfig?.find(module => {
        return module.key === props.module && module.group === (configStore.currentEstablishment || GLOBAL_GROUP_KEY)
    })
})

const canLoadGlobalConfig = computed<boolean>(() => configStore.currentEstablishment === ALL_ESTABLISHMENT_ID)
const schema = computed<KhFormulaSchemaRoot>(() => {
    return {
        children:
            moduleConfig.value?.categories.map(category => {
                return {
                    type: 'component',
                    name: category.key,
                    element: 'ClvrConfigCategory',
                    props: {
                        category: category,
                        context: moduleConfig.value?.key,
                    },
                    children: [
                        {
                            type: 'component',
                            element: 'v-row',
                            children: category.configs.map(config => {
                                return {
                                    type: 'component',
                                    element: 'v-col',
                                    props: {
                                        xl: 4,
                                        lg: 4,
                                        md: 6,
                                        sm: 12,
                                        cols: 12,
                                    },
                                    children: [
                                        {
                                            type: 'field',
                                            name: config.key,
                                            element: 'ClvrConfigElement',
                                            props: {
                                                context: moduleConfig.value?.key,
                                                id: config.key,
                                                label: config.label,
                                                description: config.description,
                                                component: config.type,
                                                componentProps: config.property,
                                                globalValue: config.global,
                                            },
                                        },
                                    ],
                                }
                            }),
                        },
                    ],
                }
            }) || [],
    }
})

const rules = computed<KhRulesEngineRule<KhFormulaManager>[]>(() => {
    const formRules: KhRulesEngineRule<KhFormulaManager>[] = []

    moduleConfig.value?.categories.forEach(category => {
        category.configs.forEach(config => {
            config.rules.forEach(rule => {
                formRules.push(
                    new KhRulesEngineRule<KhFormulaManager>({
                        id: uniqueId('config_rule_'),
                        evaluateAtInit: true,
                        conditionsEvaluationType: ConditionsEvaluationType.ANY,
                        conditions: [
                            {
                                subjectPath: rule.field,
                                comparison: LogicalComparisonOperator.EQUALS,
                                comparisonValue: rule.value,
                            },
                            {
                                subjectPath: rule.field,
                                comparison: LogicalComparisonOperator.DIFFERENT,
                                comparisonValue: rule.value,
                            },
                        ],
                        action: (data, form?) => {
                            const resultElement = form?.getElement(config.key)
                            if (resultElement) {
                                let isVisible = true
                                switch (rule.type) {
                                    case 'visible':
                                        config.rules
                                            .filter(configRule => configRule.type === rule.type)
                                            .forEach(configRule => {
                                                isVisible &&= data?.[configRule.field] === configRule.value
                                            })
                                        resultElement.isVisible = isVisible
                                        return
                                }
                            }
                        },
                    }),
                )
            })
        })
    })

    return formRules
})

watch(moduleConfig, setConfig)

onMounted(() => configStore.loadModuleConfig(ConfigElementComponentType.GLOBAL, props.module))

function getConfig(configurationModule: ConfigModule, type: ConfigElementComponentType): Record<string, unknown> {
    const configs = configurationModule?.categories.flatMap(({ configs }) => configs) || []

    const configObject = {}
    for (const config of configs) {
        configObject[config.key] = config[configValuePropertyByType[type]]
    }
    return configObject
}

function setConfig(): void {
    if (moduleConfig.value) {
        formValues.value = getConfig(
            moduleConfig.value,
            configStore.currentEstablishment ? ConfigElementComponentType.GROUP : ConfigElementComponentType.GLOBAL,
        )
    }
}

function save() {
    const formValuesApiData = {}
    for (const key in formValues.value) {
        const value = formValues.value[key]
        if (Array.isArray(value)) {
            formValuesApiData[key] = value.join('|')
        } else {
            formValuesApiData[key] = value
        }
    }

    configStore
        .saveModuleConfig(
            configStore.currentEstablishment || ConfigElementComponentType.GLOBAL,
            props.module,
            formValuesApiData,
        )
        .subscribe(
            response => {
                snackStore.snackbarManager?.addSnack(
                    new Snack($t.value('system:Configure-success-modify'), SnackColor.success),
                )
            },
            () => {
                snackStore.snackbarManager?.addSnack(
                    new Snack($t.value('clevehr:common-error_occured'), SnackColor.error),
                )
            },
        )
}

function loadGlobal() {
    if (moduleConfig.value) {
        formValues.value = getConfig(moduleConfig.value, ConfigElementComponentType.GLOBAL)
    }
}

function establishmentChange(establishmentId: Nullable<number>) {
    configStore.loadModuleConfig(null === establishmentId ? GLOBAL_GROUP_KEY : establishmentId, props.module)
}

function onCancel(): void {
    setConfig()
}

return { __sfc: true,props, configValuePropertyByType, groupStore, configStore, snackStore, $t, formValues, allEstablishment, establishments, moduleConfig, canLoadGlobalConfig, schema, rules, getConfig, setConfig, save, loadGlobal, establishmentChange, onCancel, KhSelect, ClvrPrimaryColor, ClvrFloatingCard }
}

})